/*
 * HeatCircuitTileRenderer — one heating circuit as a single tile.
 *
 * Design: this is a compound renderer that composes several primitive
 * widgets (temperature readout, setpoint stepper, mode chip, valve bar,
 * pump indicator) into ONE operator-meaningful unit. The AI-composition
 * slice picks these tiles by "one per real Heizkreis" instead of stitching
 * atomic widgets together.
 */

import { useEffect, useMemo, useState } from 'preact/hooks';
import type { HeatCircuitTileWidget } from '../widgets';
import type { IPointBindingSource, PointQuality } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { useLeafMeta } from '../LeafMetaContext';
import { formatLeafValue, formatNumber } from '../../format/leafValue';
import './HeatCircuitTileRenderer.css';

interface Props {
  widget: HeatCircuitTileWidget;
  source: IPointBindingSource;
}

export function HeatCircuitTileRenderer({ widget, source }: Props) {
  // Live-bindings — one pointSignal per configured point. Optional points
  // that aren't set produce `null` and skip both the subscription + the render.
  const flow = usePoint(source, widget.flowTemperaturePoint);
  const ret = useOptionalPoint(source, widget.returnTemperaturePoint);
  const setpoint = usePoint(source, widget.setpointPoint);
  const setpointMeta = useLeafMeta(widget.setpointPoint);
  const mode = useOptionalPoint(source, widget.modePoint);
  // Operating mode as the leaf's enum text ("3" -> "Komfort").
  const modeMeta = useLeafMeta(widget.modePoint);
  const valve = useOptionalPoint(source, widget.valvePositionPoint);
  const pump = useOptionalPoint(source, widget.pumpStatePoint);
  const room = useOptionalPoint(source, widget.roomTemperaturePoint);
  const outside = useOptionalPoint(source, widget.outsideTemperaturePoint);

  const flowVal = numeric(flow.value.value?.value);
  const retVal = numeric(ret?.value.value?.value);
  const setpointVal = numeric(setpoint.value.value?.value);
  const valveVal = numeric(valve?.value.value?.value);
  const roomVal = numeric(room?.value.value?.value);
  const outsideVal = numeric(outside?.value.value?.value);

  const flowStale = quality(flow.value.value) !== 'ok';
  const setpointStale = quality(setpoint.value.value) !== 'ok';
  const tileStale = flowStale && setpointStale;

  const min = widget.setpointMin ?? 5;
  const max = widget.setpointMax ?? 30;
  const step = widget.setpointStep ?? 0.5;

  // Read-only when the author marked it so or the leaf is not writable per
  // schema. Then there are no +/- buttons at all; disabled ones look like a
  // fault rather than a display.
  const readOnly = widget.setpointWritable === false || setpointMeta?.writable === false;
  const setpointWritable = !readOnly;

  const [pendingSetpoint, setPendingSetpoint] = useState<number | null>(null);
  const [pendingState, setPendingState] = useState<'sending' | 'rejected' | null>(null);

  async function commitSetpoint(target: number) {
    if (!setpointWritable) return;
    setPendingSetpoint(target);
    setPendingState('sending');
    const ac = new AbortController();
    try {
      for await (const ack of source.write(widget.setpointPoint, target, ac.signal)) {
        if (ack.state === 'confirmed') {
          setPendingSetpoint(null);
          setPendingState(null);
          return;
        }
        if (ack.state === 'rejected') {
          setPendingState('rejected');
          return;
        }
      }
      setPendingSetpoint(null);
      setPendingState(null);
    } catch {
      setPendingSetpoint(null);
      setPendingState(null);
    }
  }

  const shownSetpoint = pendingSetpoint ?? setpointVal;

  return (
    <div class={`hv-hc-tile${tileStale ? ' hv-hc-tile--stale' : ''}`}>
      <header class="hv-hc-tile__head">
        <div>
          <div class="hv-hc-tile__name">{widget.circuitName}</div>
          {widget.label && <div class="hv-hc-tile__label">{widget.label}</div>}
        </div>
        {mode && <ModeChip value={formatLeafValue(mode.value.value?.value, modeMeta)} />}
      </header>

      <div class="hv-hc-tile__body">
        {/* Main measurement column: room (if present) is hero, else flow. */}
        <div class="hv-hc-tile__hero">
          {roomVal != null ? (
            <>
              <div class="hv-hc-tile__hero-value">{formatNumber(roomVal, 1)}<span>°C</span></div>
              <div class="hv-hc-tile__hero-label">Raum</div>
            </>
          ) : (
            <>
              <div class="hv-hc-tile__hero-value">
                {flowVal != null ? formatNumber(flowVal, 1) : '—'}<span>°C</span>
              </div>
              <div class="hv-hc-tile__hero-label">Vorlauf</div>
            </>
          )}
        </div>

        {/* Setpoint stepper. Disabled cleanly when not writable. */}
        <div class="hv-hc-tile__setpoint">
          {!readOnly && (
            <button
              type="button"
              class="hv-hc-tile__step"
              onClick={() => shownSetpoint != null && commitSetpoint(clamp(shownSetpoint - step, min, max))}
              disabled={!setpointWritable || shownSetpoint == null || pendingState === 'sending'}
              aria-label="Sollwert verringern"
            >−</button>
          )}
          <div class="hv-hc-tile__setpoint-value">
            <span class="hv-hc-tile__setpoint-num">
              {shownSetpoint != null ? formatNumber(shownSetpoint, 1) : '—'}
            </span>
            <span class="hv-hc-tile__setpoint-unit">°C</span>
            {pendingState === 'sending' && <span class="hv-hc-tile__pending">…</span>}
            {pendingState === 'rejected' && <span class="hv-hc-tile__rejected">✕</span>}
          </div>
          {!readOnly && (
            <button
              type="button"
              class="hv-hc-tile__step"
              onClick={() => shownSetpoint != null && commitSetpoint(clamp(shownSetpoint + step, min, max))}
              disabled={!setpointWritable || shownSetpoint == null || pendingState === 'sending'}
              aria-label="Sollwert erhöhen"
            >+</button>
          )}
        </div>
      </div>

      <footer class="hv-hc-tile__foot">
        {flowVal != null && (
          <span class="hv-hc-tile__stat">
            <span class="hv-hc-tile__stat-label">Vorlauf</span>
            <span class="hv-hc-tile__stat-value">{formatNumber(flowVal, 1)}°C</span>
          </span>
        )}
        {retVal != null && (
          <span class="hv-hc-tile__stat">
            <span class="hv-hc-tile__stat-label">Rücklauf</span>
            <span class="hv-hc-tile__stat-value">{formatNumber(retVal, 1)}°C</span>
          </span>
        )}
        {valveVal != null && (
          <span class="hv-hc-tile__stat">
            <span class="hv-hc-tile__stat-label">Ventil</span>
            <span class="hv-hc-tile__stat-value">{Math.round(valveVal)} %</span>
          </span>
        )}
        {pump && (
          <span class="hv-hc-tile__stat">
            <span class="hv-hc-tile__stat-label">Pumpe</span>
            <PumpDot value={pump.value.value?.value} />
          </span>
        )}
        {outsideVal != null && (
          <span class="hv-hc-tile__stat hv-hc-tile__stat--muted">
            <span class="hv-hc-tile__stat-label">Außen</span>
            <span class="hv-hc-tile__stat-value">{formatNumber(outsideVal, 1)}°C</span>
          </span>
        )}
      </footer>
    </div>
  );
}

// ---------- Helpers -------------------------------------------------------

function usePoint(source: IPointBindingSource, id: string) {
  const sig = useMemo(() => pointSignal(source, id), [source, id]);
  useEffect(() => sig.release, [sig]);
  return sig;
}

function useOptionalPoint(source: IPointBindingSource, id: string | undefined) {
  const sig = useMemo(() => (id ? pointSignal(source, id) : null), [source, id]);
  useEffect(() => sig?.release ?? (() => {}), [sig]);
  return sig;
}

function numeric(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const parsed = Number(v);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

type QualityBucket = 'ok' | 'stale' | 'bad';

/**
 * Grobe Einordnung fuer die Kachel-Dekoration.
 *
 * Wichtig ist der Default-Zweig: seit `PointQuality` sechs Werte hat
 * (`disconnected` und `override` kamen 2026-09-03 dazu), darf hier nicht
 * mehr "alles ausser bad/stale ist ok" stehen. Ein Wert aus einem Force
 * oder von einem abgerissenen Transport wuerde sonst als frische Messung
 * gerendert. Die Entscheidung faellt deshalb ueber `isDegraded` — eine
 * Stelle, die mit dem Alphabet mitwaechst.
 */
function quality(pv: { quality: PointQuality } | undefined | null): QualityBucket {
  if (!pv) return 'stale';
  if (pv.quality === 'bad' || pv.quality === 'disconnected') return 'bad';
  return isDegraded(pv) ? 'stale' : 'ok';
}

function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

function ModeChip({ value }: { value: unknown }) {
  const s = String(value ?? '').trim();
  const label = s.length > 0 ? s : '—';
  const tone = classifyMode(s);
  return <span class={`hv-hc-tile__mode hv-hc-tile__mode--${tone}`}>{label}</span>;
}

function classifyMode(v: string): 'off' | 'reduced' | 'comfort' | 'unknown' {
  const s = v.toLowerCase();
  if (s.includes('aus') || s === 'off' || s === '0') return 'off';
  if (s.includes('absenk') || s.includes('nacht') || s.includes('reduced')) return 'reduced';
  if (s.includes('komfort') || s.includes('heiz') || s.includes('day') || s.includes('comfort')) return 'comfort';
  return 'unknown';
}

function PumpDot({ value }: { value: unknown }) {
  const on = coerceBool(value);
  const tone = on === true ? 'ok' : on === false ? 'off' : 'unknown';
  return (
    <span class={`hv-hc-tile__pump-dot hv-hc-tile__pump-dot--${tone}`}
          title={on === true ? 'läuft' : on === false ? 'aus' : 'unbekannt'} />
  );
}

function coerceBool(v: unknown): boolean | null {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (s === 'true' || s === 'on' || s === '1' || s === 'run' || s === 'running') return true;
    if (s === 'false' || s === 'off' || s === '0' || s === 'stop' || s === 'stopped') return false;
  }
  return null;
}
